import { fb } from './firebase'
import firebase from 'firebase/compat/app'
import { GameStats, Solve, setStoredStats } from './localStorage'
import { loadLocalStats } from './stats'

const USERS_TABLE = 'users'

export interface User {
  uid: string
  displayName: string | null
  email: string | null
  subscriptions?: Subscription[]
  stats?: GameStats
}

export interface Subscription {
  date_create: string
  date_last_charge?: string
  date_next_charge?: string
  date_end?: string
  plan_id: string
  subscription_id?: string
}

const db = fb.getFirestore()

export const getUserById = async (id: string) => {
  const docSnapshot = await db.collection(USERS_TABLE).doc(id).get()
  if (!docSnapshot.exists) {
    return null
  }
  return docSnapshot.data() as User
}

export const createOrUpdateUser = async (user: User) => {
  await db.collection(USERS_TABLE).doc(user.uid).set(user, { merge: true })
}

type FirestoreStats = Omit<GameStats, 'lastSyncedAt'> & {
  lastSyncedAt: firebase.firestore.FieldValue
}

export const setStats = async (uid: string, stats: GameStats) => {
  const firestoreStats: FirestoreStats = {
    winDistribution: stats.winDistribution,
    gamesFailed: stats.gamesFailed,
    currentStreak: stats.currentStreak,
    bestStreak: stats.bestStreak,
    totalGames: stats.totalGames,
    successRate: stats.successRate,
    syncedDeviceIds: stats.syncedDeviceIds ?? [],
    solves: stats.solves ?? [],
    achievements: stats.achievements ?? [],
    lastSyncedAt: firebase.firestore.FieldValue.serverTimestamp(),
  }
  await db.collection(USERS_TABLE).doc(uid).update({ stats: firestoreStats })
}

const NUM_SHARDS = 10
export const updateProblemStats = async (solve: Solve) => {
  const problemId = `${solve.mode}-${solve.solution.toLowerCase()}`
  const docRef = db.collection('problems').doc(problemId)
  const shardId = Math.floor(Math.random() * NUM_SHARDS)
  const shardRef = docRef.collection('shards').doc(shardId.toString())
  return shardRef.set(
    { [solve.guesses.toString()]: firebase.firestore.FieldValue.increment(1) },
    { merge: true }
  )
}

export async function getFirestoreUser() {
  if (!fb.isSignedIn()) {
    return null
  }
  const authUser = fb.currentAuthUser()!
  let user = await getUserById(authUser.uid)

  const authUserData: User = {
    uid: authUser.uid,
    displayName: authUser.displayName,
    email: authUser.email,
  }
  if (
    !user ||
    user.uid !== authUser.uid ||
    user.displayName !== authUser.displayName ||
    user.email !== authUser.email
  ) {
    if (!user) {
      // copy local stats for first time user
      setStoredStats(loadLocalStats(), authUser.uid)
    }
    await createOrUpdateUser(authUserData)
    if (user) {
      user.uid = authUser.uid
      user.displayName = authUser.displayName
      user.email = authUser.email
    }
  }
  return user ?? authUserData
}
